import { makeVar, useReactiveVar } from '@apollo/client'
import { RECENTLY_VISITED_AREAS_KEY } from '@backoffice-frontend/storage-keys'
import { useMemo } from 'react'

// How many area ids we keep around. The welcome page displays fewer than this
// (and filters out e.g. the welcome area itself), so we store a few extra to
// still have enough to show after filtering.
const MAX_STORED_RECENTLY_VISITED_AREAS = 10

const canUseLocalStorage = (): boolean =>
  typeof window !== 'undefined' &&
  Boolean(window.localStorage) &&
  typeof window.localStorage.getItem === 'function'

// Each user gets their own entry, so users sharing a device (e.g. drivers on a
// shared tablet) don't see each other's recently visited areas.
const getStorageKey = (userId: string): string =>
  `${RECENTLY_VISITED_AREAS_KEY}.${userId}`

const readStoredAreaIds = (userId: string): string[] => {
  if (!canUseLocalStorage()) return []
  try {
    const stored = window.localStorage.getItem(getStorageKey(userId))
    if (!stored) return []
    const parsed: unknown = JSON.parse(stored)
    if (!Array.isArray(parsed)) return []
    return parsed.filter((id): id is string => typeof id === 'string')
  } catch {
    // A corrupt value should not break the app — start from an empty list.
    return []
  }
}

// Reactive, localStorage-backed lists of recently visited area ids per user,
// most recent first. Purely client-side — no backend involved. Users are
// loaded from storage lazily on first access.
const recentlyVisitedAreaIdsByUserVar = makeVar<Record<string, string[]>>({})

const getAreaIds = (userId: string): string[] =>
  recentlyVisitedAreaIdsByUserVar()[userId] ?? readStoredAreaIds(userId)

/**
 * Records an area as recently visited for the given user: moves it to the
 * front of their list and de-duplicates, capping the stored count. Persisted to
 * localStorage so it survives reloads.
 */
export const recordRecentlyVisitedAreaId = (
  userId: string,
  areaId: string,
): void => {
  const current = getAreaIds(userId)
  const next = [areaId, ...current.filter(id => id !== areaId)].slice(
    0,
    MAX_STORED_RECENTLY_VISITED_AREAS,
  )

  // Skip the write when nothing changed (same area revisited while already at
  // the front) to avoid needless re-renders of consumers.
  const isUnchanged =
    next.length === current.length &&
    next.every((id, index) => id === current[index])
  if (isUnchanged) return

  recentlyVisitedAreaIdsByUserVar({
    ...recentlyVisitedAreaIdsByUserVar(),
    [userId]: next,
  })
  if (canUseLocalStorage()) {
    window.localStorage.setItem(getStorageKey(userId), JSON.stringify(next))
  }
}

/** Reactive list of the given user's recently visited area ids, most recent first. */
export const useRecentlyVisitedAreaIds = (userId: string): string[] => {
  const byUser = useReactiveVar(recentlyVisitedAreaIdsByUserVar)
  // Memoized so the stored fallback keeps a stable identity across renders.
  return useMemo(
    () => byUser[userId] ?? readStoredAreaIds(userId),
    [byUser, userId],
  )
}
